import { useParams } from "react-router-dom";
import BaseAppLayout from "../../layouts/BaseAppLayout/BaseAppLayout";
import Button from "../../components/Button/Button";
import ApiManager from "../../api/ApiManager";
import {
  ArrowLeft,
  CircleHelp,
  Copy,
  Languages,
  ExternalLink,
  FilePen,
  Trash,
} from "lucide-react";
import Typography from "../../components/Typography/Typography";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import FullPageModal from "../../components/FullPageModal/FullPageModal";
import React from "react";
import { useRef } from "react";

import * as pdfjsLib from "pdfjs-dist";
import { GlobalWorkerOptions } from "pdfjs-dist";

import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useAuth } from "../../store/AuthContext/AuthContext";
import ToolTip from "../../components/ToolTip/ToolTip";
import ScrollContainer from "react-indiana-drag-scroll";
import { useLocation } from "react-router-dom";

export default function DocumentDetail() {
  GlobalWorkerOptions.workerSrc = pdfWorker;

  const location = useLocation();
  const siteUrl = location.state?.siteUrl;
  const awsId = location.state?.awsId;

  const { id } = useParams();
  const navigate = useNavigate();
  const handleGoBack = () => {
    navigate(-1);
  };

  const [isPersonal, setIsPersonal] = useState(false);
  const [originalAbstract, setOriginalAbstract] = useState("");
  const [displayAbstract, setDisplayAbstract] = useState("Loading abstract...");
  const [isTranslated, setIsTranslated] = useState(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("APA");
  const [buttonText, setButtonText] = useState("Copia citazione");
  const [numPages, setNumPages] = useState(0);
  const [pdf, setPdf] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const canvasRefs = useRef([]);

  const api = new ApiManager();

  const [paperData, setPaperData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (
      !id.endsWith(".png") &&
      !id.endsWith(".jpg") &&
      !id.endsWith(".jpeg") &&
      !id.endsWith(".webp")
    ) {
      const loadPDF = async () => {
        // First check if paperData exists
        if (!paperData) {
          console.log("paperData not yet available");
          return; // Exit early, will retry when paperData is available
        }

        const pdfUrl = isPersonal
          ? paperData?.pdf_signed_url
          : paperData?.openAccessPdf?.url;

        // Log what URL we're trying to use
        console.log("Attempting to load PDF from:", pdfUrl);

        if (!pdfUrl) {
          console.error("URL del PDF non trovato!");
          setPdfError(true);
          return;
        }

        try {
          const loadingTask = pdfjsLib.getDocument(pdfUrl);
          const loadedPdf = await loadingTask.promise;

          setPdfError(false);
          setNumPages(loadedPdf.numPages);
          setPdf(loadedPdf);
        } catch (error) {
          console.error("Errore durante il caricamento del PDF:", error);
          console.error("URL tentato:", pdfUrl);
          setPdfError(true);
        }
      };

      loadPDF();
    }
  }, [paperData, isPersonal]);

  useEffect(() => {
    const renderPages = async () => {
      if (!pdf || numPages === 0) return;

      // Render pages sequentially instead of all at once
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = canvasRefs.current[pageNum - 1];
          if (!canvas) continue;

          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          // Wait for this page to complete rendering before proceeding to next page
          await page.render({ canvasContext: context, viewport }).promise;
        } catch (err) {
          console.error(`Error rendering page ${pageNum}:`, err);
        }
      }
    };

    renderPages();
  }, [pdf, numPages]);

  const { getCookie } = useAuth();
  const projectId = getCookie("project_id");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Check file extension
        if (id.endsWith(".pdf")) {
          // PDF document
          setIsPersonal(true);
          api
            .get(
              `/documents/fetch?project_id=${projectId}&document_key=sources/documents/${id}`,
              { headers: { Authorization: `Bearer ${getCookie("id_token")}` } }
            )
            .then((response) => {
              if (response.data && response.data.body) {
                setPaperData(JSON.parse(response.data.body));
                setLoading(false);
              } else {
                console.error(
                  "Error: 'body' is missing in response",
                  response.data
                );
                setPaperData(null); // o gestire l'errore in altro modo
              }
            })
            .catch((error) => {
              console.error("Error fetching PDF document:", error);
            });
        } else if (
          id.endsWith(".png") ||
          id.endsWith(".jpg") ||
          id.endsWith(".jpeg") ||
          id.endsWith(".webp")
        ) {
          // Image file
          setIsPersonal(true);
          api
            .get(
              `/images/fetch?project_id=${projectId}&image_key=sources/images/${id}`,
              { headers: { Authorization: `Bearer ${getCookie("id_token")}` } }
            )
            .then((response) => {
              setPaperData(JSON.parse(response.data.body));
              setLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching image:", error);
            });
        } else if (
          /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(id)
        ) {
          setIsPersonal(true);

          api
            .get(
              `/url/retrieve?project_id=${projectId}&url_key=${encodeURIComponent(
                siteUrl
              )}`,
              { headers: { Authorization: `Bearer ${getCookie("id_token")}` } }
            )
            .then((response) => {
              setPaperData(response.data.items);
              setLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching url:", error);
            });
        } else {
          // Semantic Scholar ID
          setIsPersonal(false);
          const url = `https://api.semanticscholar.org/graph/v1/paper/${id}?fields=paperId,title,abstract,year,venue,authors,referenceCount,citationCount,influentialCitationCount,isOpenAccess,openAccessPdf,publicationTypes,journal,fieldsOfStudy,s2FieldsOfStudy,publicationVenue,publicationDate,externalIds,references,citations,tldr`;

          const response = await api.get(url, {
            headers: {
              "x-api-key": "ZmN4EBYW1g5RJeegbMY6i7V0ymmrMubx9mAwsHwg",
            },
          });

          setPaperData(response.data);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, projectId]);

  const handleModalOpen = () => {
    setIsUploadModalOpen(true);
  };

  const handleModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const [citationContent, setCitationContent] = useState({
    APA: "",
    Vancouver: "",
    Chicago: "",
    Harvard: "",
  });

  const handleSelectStyle = (style) => {
    setSelectedStyle(style);
  };

  const handleCopyCitation = () => {
    const citation = citationContent[selectedStyle];
    navigator.clipboard.writeText(citation).then(() => {
      setButtonText("Citazione copiata!");

      setTimeout(() => {
        setButtonText("Copia citazione");
        handleModalClose();
      }, 1500);
    });
  };

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (paperData) {
      setCitationContent({
        APA: getApaCitation(paperData),
        Vancouver: getVancouverCitation(paperData),
        Chicago: getChicagoCitation(paperData),
        Harvard: getHarvardCitation(paperData),
      });
    }
  }, [paperData]);

  const getApaCitation = (paperData) => {
    const authors = paperData.authors
      ? paperData.authors.map((a) => a.name).join(", ")
      : "Autore sconosciuto";
    const year = paperData.year || "Anno sconosciuto";
    const title =
      paperData.title || paperData.file_name || "Titolo sconosciuto";
    const journal = paperData.journal
      ? paperData.journal.name
      : "Rivista sconosciuta";
    const doi = paperData.externalIds?.DOI
      ? `https://doi.org/${paperData.externalIds.DOI}`
      : "DOI non disponibile";

    return `${authors} (${year}). ${title}. *${journal}*. ${doi}`;
  };

  const getVancouverCitation = (paperData) => {
    const authors = paperData.authors
      ? paperData.authors.map((a) => a.name).join(", ")
      : "Autore sconosciuto";
    const year = paperData.year || "Anno sconosciuto";
    const title =
      paperData.title || paperData.file_name || "Titolo sconosciuto";
    const journal = paperData.journal
      ? paperData.journal.name
      : "Rivista sconosciuta";
    const volume = paperData.volume || "Volume sconosciuto";
    const pages = paperData.pages || "Pagine sconosciute";
    const doi = paperData.externalIds?.DOI
      ? `https://doi.org/${paperData.externalIds.DOI}`
      : "DOI non disponibile";

    return `${authors}. ${title}. ${journal}. ${year};${volume}(${pages}). ${doi}`;
  };

  const getChicagoCitation = (paperData) => {
    const authors = paperData.authors
      ? paperData.authors.map((a) => a.name).join(", ")
      : "Autore sconosciuto";
    const year = paperData.year || "Anno sconosciuto";
    const title =
      paperData.title || paperData.file_name || "Titolo sconosciuto";
    const journal = paperData.journal
      ? paperData.journal.name
      : "Rivista sconosciuta";
    const volume = paperData.volume || "Volume sconosciuto";
    const issue = paperData.issue || "Numero sconosciuto";
    const doi = paperData.externalIds?.DOI
      ? `https://doi.org/${paperData.externalIds.DOI}`
      : "DOI non disponibile";

    return `${authors}. ${year}. ${title}. *${journal}* ${volume}(${issue}): pages. ${doi}`;
  };

  const getHarvardCitation = (paperData) => {
    const authors = paperData.authors
      ? paperData.authors.map((a) => a.name).join(", ")
      : "Autore sconosciuto";
    const year = paperData.year || "Anno sconosciuto";
    const title =
      paperData.title || paperData.file_name || "Titolo sconosciuto";
    const journal = paperData.journal
      ? paperData.journal.name
      : "Rivista sconosciuta";
    const volume = paperData.volume || "Volume sconosciuto";
    const pages = paperData.pages || "Pagine sconosciute";
    const doi = paperData.externalIds?.DOI
      ? `https://doi.org/${paperData.externalIds.DOI}`
      : "DOI non disponibile";

    return `${authors}, ${year}, *${title}*, ${journal}, ${volume} (${pages}). Disponibile al: ${doi}`;
  };

  const displayTitle = isPersonal
    ? paperData?.file_name || paperData?.title
    : paperData?.title || "";

  useEffect(() => {
    if (paperData && paperData.abstract) {
      setOriginalAbstract(paperData.abstract);
      setDisplayAbstract(paperData.abstract);
    }
  }, [paperData]);

  const displayTldr =
    paperData && paperData.tldr
      ? paperData.tldr.text || paperData.tldr
      : "Loading Sintesi intelligente...";

  const deleteDocument = () => {
    const id_token = getCookie("id_token");

    if (id.endsWith(".pdf")) {
      api
        .delete(
          `/documents/delete`,
          {
            project_id: projectId,
            document_key: "sources/documents/" + id,
          },
          {
            headers: { Authorization: `Bearer ${id_token}` },
          }
        )
        .then((response) => {
          navigate(-1);
        })
        .catch((error) => {
          console.error("Error deleting document:", error);
        });
    } else if (
      id.endsWith(".png") ||
      id.endsWith(".jpg") ||
      id.endsWith(".jpeg") ||
      id.endsWith(".webp")
    ) {
      api
        .delete(
          `/images/delete`,
          {
            project_id: projectId,
            image_key: "sources/images/" + id,
          },
          {
            headers: { Authorization: `Bearer ${id_token}` },
          }
        )
        .then((response) => {
          navigate(-1);
        })
        .catch((error) => {
          console.error("Error deleting image:", error);
        });
    } else if (
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(id)
    ) {
      api
        .delete(
          `/url/delete`,
          {
            project_id: projectId,
            url_key: siteUrl,
          },
          {
            headers: {
              Authorization: `Bearer ${id_token}`,
            },
          }
        )
        .then((response) => {
          navigate(-1);
        })
        .catch((error) => {
          console.error("Error deleting URL:", error);
        });
    } else {
      const id_token = getCookie("id_token");
      const projectId = getCookie("project_id");

      const payload = {
        project_id: projectId,
        document_id: awsId,
      };

      api
        .delete("/semanticdocuments/delete", payload, {
          headers: {
            Authorization: `Bearer ${id_token}`,
          },
        })
        .then((response) => {
          navigate(-1);
        })
        .catch((error) => {
          console.error("Error removing document:", error);
        });
    }
  };

  const tags = (() => {
    try {
      const parsed = JSON.parse(paperData?.tags || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const handleTranslateAbstract = () => {
    if (!isTranslated) {
      const id_token = getCookie("id_token");
      const api = new ApiManager();

      const payload = {
        testo: originalAbstract,
        lingua: "it",
      };

      api
        .post("/translate", payload, {
          headers: {
            Authorization: `Bearer ${id_token}`,
          },
        })
        .then((response) => {
          setIsTranslated(true);
          setDisplayAbstract(response.data.traduzione);
        })
        .catch((error) => {
          console.error("Error during translation:", error);
        });
    } else {
      // Se è già tradotto → torna all'originale
      setIsTranslated(false);
      setDisplayAbstract(originalAbstract);
    }
  };

  return (
    <BaseAppLayout headerText="Libreria">
      <div className="w-full flex flex-col gap-6 md:gap-9 pt-10 md:pb-20 pb-12">
        {loading && (
          <div className="px-6 md:px-[120px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-44" />
            <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-10" />
            <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-[20rem] md:h-[50rem]" />
            <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-44" />
            <div className="col-span-full lg:col-span-6 xl:col-span-4 rounded-[12px] bg-gray-100 animate-pulse h-10" />
          </div>
        )}

        {!loading && (
          <>
            <div className="px-6 md:px-[120px] flex justify-between items-center">
              <Button
                icon={<ArrowLeft size={16} />}
                size="md"
                type="bw"
                onClick={handleGoBack}
              />
              <div className="flex gap-4">
                <Button
                  icon={<Trash size={20} color="red" />}
                  size="md"
                  type="bw"
                  onClick={deleteDocument}
                />
                <Button
                  text="Cita Fonte"
                  ricon={<FilePen size={20} />}
                  size="sm"
                  type="bw"
                  onClick={handleModalOpen}
                />
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="px-6 md:px-[120px]">
                <Typography
                  size="28"
                  desktopSize="48"
                  weight="sb"
                  family="serif"
                >
                  {displayTitle}
                </Typography>
              </div>

              <div className="w-full overflow-x-scroll scrollbar-hidden">
                <ScrollContainer horizontal>
                  <div className="w-fit flex gap-3 md:gap-4 px-6 md:px-[120px] items-center">
                    {!isPersonal ? (
                      <>
                        {/* Authors Section */}
                        {paperData?.authors?.length > 0 && (
                          <>
                            {paperData.authors.map((a, index) => (
                              <React.Fragment key={a.authorId}>
                                <Typography
                                  noWrap
                                  size="12"
                                  desktopSize="16"
                                  weight="md"
                                  color="gray-600"
                                >
                                  {a.name}
                                </Typography>
                                {index < paperData.authors.length - 1 && (
                                  <div className="w-[2px] h-4 md:h-5 bg-gray-200" />
                                )}
                              </React.Fragment>
                            ))}
                          </>
                        )}

                        {/* Publication Date */}
                        {(paperData?.publicationDate || paperData?.year) && (
                          <>
                            <div className="w-[2px] h-4 md:h-5 bg-gray-200" />
                            <Typography
                              noWrap
                              size="12"
                              desktopSize="16"
                              weight="md"
                              color="gray-600"
                            >
                              {paperData.publicationDate
                                ? new Date(
                                  paperData.publicationDate
                                ).toLocaleDateString("it-IT", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                })
                                : paperData.year || "Data non disponibile"}
                            </Typography>
                          </>
                        )}

                        {/* Journal Name */}
                        {paperData?.journal?.name && (
                          <>
                            <div className="w-[2px] h-4 md:h-5 bg-gray-200" />
                            <Typography
                              noWrap
                              size="12"
                              desktopSize="16"
                              weight="md"
                              color="gray-600"
                            >
                              {paperData.journal.name}
                            </Typography>
                          </>
                        )}

                        {/* Fields of Study */}
                        {paperData?.fieldsOfStudy ||
                          paperData?.s2FieldsOfStudy ? (
                          <>
                            <div className="w-[2px] h-4 md:h-5 bg-gray-200" />

                            {/* Uniamo fieldsOfStudy e s2FieldsOfStudy, e filtriamo i duplicati */}
                            {[
                              ...(paperData.fieldsOfStudy || []).map(
                                (field) => ({
                                  category: field,
                                  source: "fieldsOfStudy",
                                })
                              ),
                              ...(paperData.s2FieldsOfStudy || []),
                            ]
                              .filter(
                                (value, index, self) =>
                                  index ===
                                  self.findIndex(
                                    (t) => t.category === value.category
                                  )
                              )
                              .map((field, index) => (
                                <React.Fragment key={index}>
                                  <Typography
                                    noWrap
                                    size="12"
                                    desktopSize="16"
                                    weight="md"
                                    color="gray-600"
                                  >
                                    {field.category}
                                  </Typography>
                                  {index <
                                    (paperData.fieldsOfStudy || []).length -
                                    1 && (
                                      <div className="w-[2px] h-4 md:h-5 bg-gray-200" />
                                    )}
                                  {index <
                                    (paperData.s2FieldsOfStudy || []).length -
                                    1 && (
                                      <div className="w-[2px] h-4 md:h-5 bg-gray-200" />
                                    )}
                                </React.Fragment>
                              ))}
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {tags.length > 0 && (
                          <>
                            {tags.map((tag, index) => (
                              <React.Fragment key={index}>
                                <Typography
                                  noWrap
                                  size="12"
                                  desktopSize="16"
                                  weight="md"
                                  color="gray-600"
                                >
                                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                                </Typography>
                                {index < tags.length - 1 && (
                                  <div className="w-[2px] h-4 md:h-5 bg-gray-200" />
                                )}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </ScrollContainer>
              </div>

              <div className="px-6 md:px-[120px] flex flex-col gap-4">
                <div className="relative flex-grow h-auto max-h-60 md:max-h-80 lg:max-h-144 flex items-start justify-center bg-gray-100 border-2 border-gray-150 rounded-[8px] md:rounded-[12px] px-6 pt-6 md:px-9 md:pt-9 overflow-y-auto scrollbar-hidden group pb-6 md:pb-9">
                  {id.endsWith(".pdf") && (
                    <div className={`flex flex-col gap-6 md:gap-9`}>
                      {Array.from({ length: numPages }, (_, i) => (
                        <canvas
                          key={i}
                          ref={(el) => (canvasRefs.current[i] = el)}
                          className="rounded-[8px] w-full"
                        />
                      ))}
                    </div>
                  )}

                  {(id.endsWith(".png") ||
                    id.endsWith(".jpg") ||
                    id.endsWith(".jpeg") ||
                    id.endsWith(".webp")) && (
                      <div className="w-full max-h-full">
                        <img
                          src={paperData?.signed_url}
                          alt="Document"
                          className="w-full object-top rounded-[8px] h-full"
                        />
                      </div>
                    )}

                  {/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(
                    id
                  ) &&
                    siteUrl !== "" &&
                    paperData?.image && (
                      <div className="w-full max-h-full">
                        <img
                          src={paperData?.image}
                          alt="Url"
                          className="w-full object-top rounded-[8px] h-full"
                        />
                      </div>
                    )}

                  {!id.endsWith(".pdf") &&
                    !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(
                      id
                    ) &&
                    !id.endsWith(".png") &&
                    !id.endsWith(".jpg") &&
                    !id.endsWith(".jpeg") &&
                    !id.endsWith(".webp") &&
                    (pdfError === true ? (
                      <div className="flex flex-col gap-4 items-center justify-center h-full overflow-y-hidden">
                        <Typography
                          size="20"
                          weight="rg"
                          color="gray-600"
                          align="center"
                        >
                          Non è possibile visualizzare il documento.
                        </Typography>
                        <img
                          src="/assets/images/illustrations/searching_document.png"
                          alt="Document Not Found"
                          className="w-64 md:w-48 h-auto"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4 items-center justify-center h-full overflow-y-hidden">
                        {Array.from({ length: numPages }, (_, i) => (
                          <canvas
                            key={i}
                            ref={(el) => (canvasRefs.current[i] = el)}
                            className="rounded-[8px] w-full"
                          />
                        ))}
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <>
              <div className="px-6 md:px-[120px] flex flex-col gap-3">
                <div className="flex gap-3 items-center">
                  <Typography size="20" desktopSize="24" weight="sb">
                    Abstract
                  </Typography>
                  <div className="h-[20px]" onClick={handleTranslateAbstract}>
                    <ToolTip text="Traduci l'abstract in italiano">
                      <Languages
                        size={20}
                        className={`cursor-pointer text-${isTranslated ? "accent" : "gray-600"}`}
                      />
                    </ToolTip>
                  </div>
                </div>
                <div className="flex gap-4 items-stretch">
                  <div className="w-[2px] flex-none rounded-full bg-gray-200" />
                  <Typography
                    weight="rg"
                    size="16"
                    desktopSize="20"
                    color="gray-600"
                  >
                    {displayAbstract}
                  </Typography>
                </div>
              </div>

              {paperData && paperData.tldr && (
                <div className="px-6 md:px-[120px] flex flex-col gap-3">
                  <div className="flex gap-3 items-center">
                    <Typography size="20" desktopSize="24" weight="sb">
                      Sintesi intelligente
                    </Typography>
                    <div className="text-gray-600 flex">
                      <ToolTip text="Il succo del contenuto, in poche parole.">
                        <CircleHelp
                          size={20}
                          className="cursor-pointer text-gray-600"
                        />
                      </ToolTip>
                    </div>
                  </div>
                  <div className="flex gap-4 items-stretch">
                    <div className="w-[2px] flex-none rounded-full bg-gray-200" />
                    <Typography
                      weight="rg"
                      size="16"
                      desktopSize="20"
                      color="gray-600"
                    >
                      {displayTldr}
                    </Typography>
                  </div>
                </div>
              )}
            </>

            {((paperData &&
              (paperData.openAccessPdf?.url ||
                paperData.externalIds?.DOI ||
                paperData.externalIds?.PubMedCentral ||
                paperData.externalIds?.PubMed ||
                paperData.paperId ||
                id.endsWith(".pdf"))) ||
              /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(
                id
              )) && (
                <div className="w-full overflow-x-scroll scrollbar-hidden">
                  <div className="w-fit flex gap-3 md:gap-4 px-6 md:px-[120px] items-center">
                    {paperData && paperData.openAccessPdf?.url && (
                      <Button
                        size={isMobile ? "sm" : "md"}
                        type="color"
                        text="Scarica PDF"
                        ricon={<ExternalLink size={20} />}
                        onClick={() =>
                          window.open(paperData.openAccessPdf.url, "_blank")
                        }
                      />
                    )}

                    {paperData && paperData.externalIds?.DOI && (
                      <Button
                        size={isMobile ? "sm" : "md"}
                        type="bw"
                        text="Visualizza DOI"
                        ricon={<ExternalLink size={20} />}
                        onClick={() =>
                          window.open(
                            `https://doi.org/${paperData.externalIds.DOI}`,
                            "_blank"
                          )
                        }
                      />
                    )}

                    {paperData && paperData.externalIds?.PubMedCentral && (
                      <Button
                        size={isMobile ? "sm" : "md"}
                        type="bw"
                        text="Visualizza PubMedCentral"
                        ricon={<ExternalLink size={20} />}
                        onClick={() =>
                          window.open(
                            `https://pubmed.ncbi.nlm.nih.gov/${paperData.externalIds.PubMedCentral}`,
                            "_blank"
                          )
                        }
                      />
                    )}

                    {paperData && paperData.externalIds?.PubMed && (
                      <Button
                        size={isMobile ? "sm" : "md"}
                        type="bw"
                        text="Visualizza PubMed"
                        ricon={<ExternalLink size={20} />}
                        onClick={() =>
                          window.open(
                            `https://pubmed.ncbi.nlm.nih.gov/${paperData.externalIds.PubMed}`,
                            "_blank"
                          )
                        }
                      />
                    )}

                    {paperData && paperData.paperId && (
                      <Button
                        size={isMobile ? "sm" : "md"}
                        type="bw"
                        text="Visualizza su Semantic"
                        ricon={<ExternalLink size={20} />}
                        onClick={() =>
                          window.open(
                            `https://www.semanticscholar.org/paper/${paperData.paperId}`,
                            "_blank"
                          )
                        }
                      />
                    )}

                    {id.endsWith(".pdf") && (
                      <div className="flex w-full justify-end">
                        <Button
                          size={isMobile ? "sm" : "md"}
                          type="bw"
                          ricon={<ExternalLink size={20} />}
                          text="Apri in una nuova scheda"
                          onClick={() =>
                            window.open(paperData?.pdf_signed_url, "_blank")
                          }
                        />
                      </div>
                    )}

                    {/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(
                      id
                    ) &&
                      siteUrl !== "" && (
                        <div className="flex w-full justify-end">
                          <Button
                            size={isMobile ? "sm" : "md"}
                            type="bw"
                            ricon={<ExternalLink size={20} />}
                            text="Apri in una nuova scheda"
                            onClick={() => window.open(siteUrl, "_blank")}
                          />
                        </div>
                      )}
                  </div>
                </div>
              )}
          </>
        )}
      </div>

      <FullPageModal
        open={isUploadModalOpen}
        onClose={handleModalClose}
        title="Cita questa fonte"
      >
        <div className="flex flex-col gap-6 h-full">
          <div className="flex gap-3 items-center w-full">
            {["APA", "Vancouver", "Chicago", "Harvard"].map((style) => (
              <div
                key={style}
                className={`cursor-pointer flex py-[6px] md:py-2 w-full rounded-[8px] items-center justify-center ${selectedStyle === style
                  ? "bg-gray-150 border-2 border-gray-200"
                  : "bg-transparent border-2 border-transparent"
                  }`}
                onClick={() => handleSelectStyle(style)}
              >
                <Typography
                  weight="sb"
                  size="12"
                  desktopSize="16"
                  color="black"
                  align="center"
                >
                  {style}
                </Typography>
              </div>
            ))}
          </div>

          <div className="w-full h-full border-2 border-gray-200 bg-white rounded-[12px] px-5 py-4">
            <Typography size="16" weight="rg" color="black">
              {citationContent[selectedStyle]}
            </Typography>
          </div>

          <Button
            width="full"
            size="md"
            type="color"
            text={buttonText}
            ricon={<Copy size={20} />}
            onClick={handleCopyCitation}
          />
        </div>
      </FullPageModal>
    </BaseAppLayout>
  );
}
